import { useEffect, useState } from "react";
import { PAGE_HEROES } from "../content/page-copy";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  SectionHead,
  Card,
  TwoSidedCut,
  buttonStyles,
  fadeUp,
  stagger,
  inView,
} from "@comai-portal/design-system";
import { pricingAPI } from "../services/api";

/** Format a fraction as a percent string, or null if not set (0/undefined). */
function pct(fraction: number | undefined): string | null {
  if (!fraction || fraction <= 0) return null;
  const v = fraction * 100;
  return `${Number.isInteger(v) ? v : v.toFixed(2).replace(/\.?0+$/, "")}%`;
}

/**
 * PricingPage — the two-sided-cut explainer, rebuilt on the design system. (CO 069)
 *
 * Reflects CO 068: two flat margins (Com margin + Stripe margin), the same for
 * every business, no tiers, rent zero. Threads to /coms (the currency) and
 * /trust (why the cut is honest). No fabricated numbers — Stripe at cost is
 * shown; the ComOS percents are stated as form until the operator sets them.
 */

const TEACH = [
  {
    title: "Set up everything free. Pay only when money moves.",
    body: "Stand up your store, turn on the agent fleet, get found by every AI — and spend nothing. There's no rent, no subscription, no seat fee. An idle account costs you nothing, this month and every month.",
    detail:
      "Every cost arrives after value has already moved through your business — your spend or your sale. The cost is always behind the value, never ahead of it. You can't be underwater to us.",
  },
  {
    title: "A Com is single-use. Made, spent once, destroyed.",
    body: "Everything your business does on ComOS spends Coms — an agent buying shipping, sending email, making a purchase. A Com is perishable: it does one job, then it's gone. You buy roughly what you'll use; it can't be hoarded.",
    detail:
      "Because a Com can't pile up, our cut rides your actual spending — transaction by transaction, never a flat guess, never a charge for money you're holding. Each Com is backed 1:1 by a real dollar; unspent Coms expire and their backing comes back to you as a claim — nothing is ever charged for holding.",
  },
  {
    title: "When you spend, a small Com margin rides the Coms you use.",
    body: "Every dollar you turn into Coms carries a small, named margin — a cut of your operating spend, at a rate you can always see. The more efficiently you run, the less you spend, the less we make. We're on your side of that math.",
    detail:
      "The margin is part of the price of a Com when you buy it — a visible line, not a hidden spread. It's the same rate for every business, and it gets smaller as the network grows.",
  },
  {
    title:
      "When you sell, Stripe is passed through at cost — plus an explicit Stripe margin.",
    body: "When a customer pays you, the payment runs through Stripe. We pass Stripe's fee straight through at cost — you'd pay that to Stripe directly anyway — and add one clearly-stated ComOS percent on top. A cut of your sales, fully itemized.",
    detail:
      "Stripe's published rate is passed through unchanged and shown. The ComOS percent on top is shown separately on every transaction. Nothing is blended into a single mystery number.",
  },
  {
    title: "Two settings. The same cut for every business.",
    body: "That's the whole model: a Com margin on what flows out, a Stripe margin on what flows in. No tiers, no plan ladder, no volume discount — the same rate whether you do ten thousand or ten million. It falls for everyone as the network grows.",
    detail:
      "We don't reward size with a lower rate, because that would make 'what do I pay?' answer 'it depends.' The same legible cut for everyone is the honest version.",
  },
];

const PROMISE = [
  {
    label: "Named & visible",
    body: "The Com margin is a line; Stripe is shown at cost; the ComOS percent is explicit. Nothing blended.",
  },
  {
    label: "On flow, never on existence",
    body: "We charge when money moves, never for holding or for time passing.",
  },
  { label: "Small", body: "Each cut is low — the cost of rails, not a levy." },
  {
    label: "The same for everyone",
    body: "One rate, every business; it falls for all as the network grows. No volume discount, no tiers.",
  },
];

const FAQ = [
  {
    q: "What do I pay if I do nothing?",
    a: "Nothing. No rent, no subscription, no seat fee. ComOS only earns when money moves through your business. An idle account costs you $0.",
  },
  {
    q: "How does ComOS make money off me?",
    a: "Two small, flat settings on flow. A Com margin on what you spend to run the business, and a Stripe margin (on top of Stripe-at-cost) on what you sell. Out and in — nothing for sitting still.",
  },
  {
    q: "Is the rate different at higher volume?",
    a: "No. It's the same cut for every business, and it falls for everyone as the network grows. No tiers, no plan ladder, no volume discount — so 'what do I pay?' always has one answer.",
  },
  {
    q: "Is pricing different if I run ComOS on my own hardware?",
    a: "No. The two cuts are the whole fee model — in our cloud or on a node you own. Installing costs nothing, updates ride the signed release train free, and your node spends and earns the same Coms as every other world. One economy, wherever your world lives.",
  },
  {
    q: "What is a Com?",
    a: "A single-use, perishable unit backed 1:1 by a real dollar. Made when you spend, used once, destroyed. It can't be hoarded — which is what lets our cut ride your real spending.",
  },
  {
    q: "Is the agent fleet really free?",
    a: "Free to have and to run — watching, deciding, recommending all cost nothing. You pay only when an agent takes a real action that consumes a service, at the same small Com margin as any expense.",
  },
];

const HERO = PAGE_HEROES['pricing']!;

export function PricingPage() {
  // Live fee config (CO 068). comMargin/stripeMargin are fractions; 0 = not set
  // → we show the FORM, never a fabricated number.
  const [comMargin, setComMargin] = useState<number | undefined>();
  const [stripeMargin, setStripeMargin] = useState<number | undefined>();
  const [stripeCost, setStripeCost] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    pricingAPI.getConfig().then((cfg) => {
      if (!alive || !cfg) return;
      setComMargin(cfg.comMargin);
      setStripeMargin(cfg.stripeMargin);
      if (cfg.stripePassthrough && cfg.stripeDomesticPercent) {
        // Money keeps its cents: "$0.30", never "$0.3" (CO 551 — the fee is live).
        const flat = cfg.stripeDomesticFlat ? ` + $${cfg.stripeDomesticFlat.toFixed(2)}` : "";
        setStripeCost(`${cfg.stripeDomesticPercent}%${flat}`);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  const comPct = pct(comMargin);
  const stripePct = pct(stripeMargin);

  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      {/* Hero */}
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              {HERO.eyebrow}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5.5vw,3.75rem)] leading-[1.08] text-balance"
            >
              {HERO.h1}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              No subscription. No rent. ComOS takes a small, transparent cut of
              the money moving through your business — out and in — and nothing
              when you're idle.
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      {/* The diagram */}
      <Section tint="sunk">
        <Container>
          <SectionHead
            align="center"
            eyebrow="How we get paid"
            title="A small cut of the money that flows — out and in."
          />
          <motion.div {...inView} variants={fadeUp} className="mt-12">
            <Card className="max-w-4xl mx-auto">
              <TwoSidedCut />
            </Card>
          </motion.div>

          {/* The actual numbers — live from config (CO 068). Real % when set;
              the form when not. Never a fabricated figure. */}
          <motion.div {...inView} variants={fadeUp} className="mt-8">
            <div className="max-w-3xl mx-auto grid gap-4 sm:grid-cols-2">
              <Card className="text-center">
                <p className="ds-eyebrow mb-2">Money out — the Com margin</p>
                <p className="text-display-md text-signal dark:text-signal-bright">
                  {comPct ?? "a small %"}
                </p>
                <p className="text-sm text-mute dark:text-mute-soft mt-2">
                  {comPct
                    ? "added when you turn dollars into Coms to run your business — the same rate for everyone."
                    : "a small, named markup when you turn dollars into Coms — the same rate for everyone."}
                </p>
              </Card>
              <Card className="text-center">
                <p className="ds-eyebrow mb-2">Money in — the Stripe margin</p>
                <p className="text-display-md text-flow dark:text-flow-bright">
                  {stripePct ?? "an explicit %"}
                </p>
                <p className="text-sm text-mute dark:text-mute-soft mt-2">
                  on top of Stripe at cost
                  {stripeCost ? ` (${stripeCost}, passed through)` : ""} when a
                  customer pays you — shown as its own line, never blended.
                </p>
              </Card>
            </div>
            <p className="text-center text-xs text-mute dark:text-mute-soft mt-4">
              {comPct || stripePct
                ? "Flat for every business. No tiers, no volume discount — it falls for everyone as the network grows."
                : "Flat for every business — the exact rates are shown here the moment they're set; no tiers, no hidden spread."}
            </p>
          </motion.div>
        </Container>
      </Section>

      {/* The teach */}
      <Section>
        <Container width="prose">
          <SectionHead
            eyebrow="How it works"
            title="Everything that creates flow is free. We charge only for the flow."
          />
          <div className="mt-10 space-y-3">
            {TEACH.map((item, i) => (
              <TeachRow
                key={item.title}
                n={i + 1}
                {...item}
                defaultOpen={i < 2}
              />
            ))}
          </div>
          <p className="mt-8 text-sm text-mute dark:text-mute-soft">
            Coms are the currency underneath all of this.{" "}
            <Link
              to="/coms"
              className="text-signal dark:text-signal-bright underline underline-offset-2"
            >
              See how the currency works →
            </Link>
          </p>
        </Container>
      </Section>

      {/* Transparency promise — threads to /trust */}
      <Section tint="sunk">
        <Container>
          <SectionHead eyebrow="The promise" title="Every cut, in the open" />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-10 grid gap-5 md:grid-cols-2 max-w-3xl mx-auto"
          >
            {PROMISE.map((p) => (
              <motion.div key={p.label} variants={fadeUp}>
                <Card className="h-full">
                  <h3 className="text-display-sm mb-1.5">{p.label}</h3>
                  <p className="text-mute dark:text-mute-soft text-sm">
                    {p.body}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
          <div className="mt-10 text-center">
            <Link to="/trust" className={buttonStyles({ variant: "outline" })}>
              Why you can verify all of this →
            </Link>
          </div>
        </Container>
      </Section>

      {/* FAQ */}
      <Section>
        <Container width="prose">
          <SectionHead
            eyebrow="FAQ"
            title="Questions you'd ask before paying"
          />
          <div className="mt-10 space-y-3">
            {FAQ.map((item) => (
              <TeachRow key={item.q} title={item.q} body={item.a} />
            ))}
          </div>
        </Container>
      </Section>

      {/* Close */}
      <Section tint="ink">
        <Container className="text-center">
          <motion.div
            {...inView}
            variants={fadeUp}
            className="max-w-2xl mx-auto"
          >
            <h2 className="text-display-lg text-balance text-paper">
              Set it all up free. Pay only when money moves.
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/start"
                className={buttonStyles({ variant: "primary", size: "lg" })}
              >
                Start building — free
              </Link>
              <Link
                to="/support"
                className={buttonStyles({
                  variant: "outline",
                  size: "lg",
                  className:
                    "border-paper/25 text-paper hover:text-white hover:border-paper",
                })}
              >
                Talk to us
              </Link>
            </div>
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}

function TeachRow({
  n,
  title,
  body,
  detail,
  defaultOpen = false,
}: {
  n?: number;
  title: string;
  body: string;
  detail?: string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-mute-line/70 dark:border-ink-line">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start gap-3 py-4 text-left"
      >
        {n != null && (
          <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-signal/40 text-signal dark:text-signal-bright text-xs font-grotesk font-semibold">
            {n}
          </span>
        )}
        <span className="flex-1 font-grotesk font-semibold text-lg text-ink dark:text-paper">
          {title}
        </span>
        <span
          className={
            "text-mute transition-transform " + (open ? "rotate-180" : "")
          }
        >
          ▾
        </span>
      </button>
      {open && (
        <div className="pb-5 pl-9 pr-4 space-y-2">
          <p className="text-ink/90 dark:text-paper/85">{body}</p>
          {detail && (
            <p className="text-sm text-mute dark:text-mute-soft">{detail}</p>
          )}
        </div>
      )}
    </div>
  );
}
